import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { createColumnHelper } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { PERMISSION } from 'src/features/auth';
import CashCollectionSidePanel from 'src/features/cashCollections/components/CashCollectionSidePanel';
import { CellCashCollectionActions } from 'src/features/cashCollections/components/CellCashCollectionActions';
import { EditCashCollection } from 'src/features/cashCollections/components/forms/EditCashCollection';
import EditCashCollectionStatus from 'src/features/cashCollections/components/forms/EditCashCollectionStatus';
import { NewCashCollection } from 'src/features/cashCollections/components/forms/NewCashCollection';
import { CASH_COLLECTION_RESOURCE } from 'src/features/cashCollections/hooks/useCreateCashCollection';
import { useDeleteCashCollection } from 'src/features/cashCollections/hooks/useDeleteCashCollection';
import { useListCashCollections } from 'src/features/cashCollections/hooks/useGetListCashCollections';
import { useUpdateCashCollection } from 'src/features/cashCollections/hooks/useUpdateCashCollection';
import { badgeCollectionStatusOptions } from 'src/features/cashCollections/utils/constants';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useListAllParkings } from 'src/features/payStations/api/useListAllParkings';
import { useListPayStations } from 'src/features/payStations/api/useListPaystations';
import payStationsMessages from 'src/features/payStations/utils/messages';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import useFilters from 'src/hooks/useFilters';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useMultiRowSelection } from 'src/hooks/useMultiRowSelection';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import { useSingleRowSelection } from 'src/hooks/useSingleRowSelection';
import useSorting from 'src/hooks/useSorting';
import { queryClient } from 'src/lib/react-query';
import BulkActionsDropdown from 'src/registry/components/blocks/BulkActionsDropdown';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    type DataTableBulkActionType,
    DataTableColumnHeader,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellAction } from 'src/registry/components/table/cells/CellAction';
import { CellCheckbox } from 'src/registry/components/table/cells/CellCheckbox';
import DataTableColumnGenericHeaderDefinition from 'src/registry/components/table/DataTableColumnGenericHeaderDefinition';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { toast } from 'src/registry/hooks/UseToast';
import { generalErrorMessages, generalMessages, validationMessagesMap } from 'src/utils/messages';

import type { CashCollections } from 'src/features/cashCollections/types/cashCollections';

const CASH_COLLECTIONS_SIDE_PANEL = 'cash-collections-side-panel';

export const formSchema = z.object({
    status: z.enum(['paid', 'collected'], { message: validationMessagesMap.validationErrorRequired }),
});

const CashCollectionsTable = () => {
    const { formatMessage, formatNumber } = useIntl();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'date', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({
        globalFilterKey: 'parking.name',
    });

    const [selectedCashCollection, setSelectedCashCollection] = React.useState<CashCollections | undefined>(undefined);
    const [pendingAction, setPendingAction] = React.useState<DataTableBulkActionType | null>(null);

    const permissionCashCollections = useHasPermissions([PERMISSION.CASH_COLLECTIONS]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            status: undefined,
        },
    });

    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();

        const today = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);

        return { afterDate: lastWeek, beforeDate: tomorrow };
    });

    const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const today = new Date();
    const beforeDateForApi = new Date(today);
    beforeDateForApi.setDate(today.getDate() + 1);

    const { data, isError, isLoading } = useListCashCollections({
        date_after: formatLocalDate(dateTime.afterDate),
        date_before: formatLocalDate(dateTime.beforeDate),
        ...queryFiltersOptions,
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...querySortingOptions,
    });

    const { data: payStationsData } = useListPayStations({});
    const { data: parkingsData } = useListAllParkings({});
    const { mutate: deleteCashCollection } = useDeleteCashCollection({
        onSuccess: () => {
            setIsDeleteModalOpen(false);
            void queryClient.invalidateQueries({
                queryKey: [CASH_COLLECTION_RESOURCE],
            });
            toast({
                title: formatMessage(cashCollectionsMessages.cashCollectionDeleted),
                description: formatMessage(cashCollectionsMessages.cashCollectionDeletedSuccessfully),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();
    const {
        rowSelectionState: multiRowState,
        setRowSelectionState: setMultiRowState,
        selectedIds,
    } = useMultiRowSelection();

    const { mutateAsync } = useUpdateCashCollection({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [CASH_COLLECTION_RESOURCE],
            });
            setPendingAction(null);
            form.reset();
        },
        onError: (e) => {
            const errorData = e.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                variant: 'destructive',
                title: errorCode,
                description: errorMessage,
            });
        },
    });

    const handleMultiSelectUpdateStatus = async (ids: string[], status?: 'paid' | 'collected') => {
        if (!status) return;

        try {
            await Promise.all(ids.map((id) => mutateAsync({ id, status })));
            toast({
                title: formatMessage(cashCollectionsMessages.cashCollectionUpdated),
                description: formatMessage(cashCollectionsMessages.cashCollectionStatusUpdatedSuccessfully),
            });
        } catch (error) {
            console.error('Error: ', error);
        }
    };
    const bulkActions = [
        {
            label: formatMessage(cashCollectionsMessages.changeStatus),
            id: 'edit_status',
            icon: ICON_MAP.EDIT,
            onSelect: () => handleMultiSelectUpdateStatus(selectedIds, form.getValues('status')),
        },
    ];

    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <div className="flex flex-row gap-2">
                    <DataTableDateDropdown
                        setValue={setDateTime}
                        inline
                        afterDate={dateTime.afterDate}
                        beforeDate={dateTime.beforeDate}
                    />

                    {bulkActions && (
                        <BulkActionsDropdown
                            bulkActions={bulkActions}
                            selectedIds={selectedIds}
                            setPendingAction={setPendingAction}
                        />
                    )}
                </div>,
            ),
        [
            setDateTime,
            dateTime.afterDate,
            dateTime.beforeDate,
            querySortingOptions,
            queryFiltersOptions,
            selectedIds,
            setPendingAction,
        ],
    );

    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);

    const singleRowSelection = React.useCallback((rowId: string) => {
        setRowSelectionState({ [rowId]: true });
    }, []);

    const cashCollectionTypeTranslationMap = {
        collect: formatMessage(cashCollectionsMessages.cashCollectionCollect),
        load: formatMessage(cashCollectionsMessages.cashCollectionLoad),
    };

    const columnHelper = createColumnHelper<CashCollections>();

    const handleCashCollectionModal = (
        type: 'edit' | 'delete',
        collection?: CashCollections,
        isOpen: boolean = false,
    ) => {
        setSelectedCashCollection(collection);
        if (type === 'edit') {
            setIsEditModalOpen(isOpen);
        } else {
            setIsDeleteModalOpen(isOpen);
        }
    };

    const openEditModal = (collection: CashCollections) => handleCashCollectionModal('edit', collection, true);
    const openDeleteModal = (collection: CashCollections) => handleCashCollectionModal('delete', collection, true);

    const columns = [
        columnHelper.display({
            id: 'select',
            cell: (ctx) => <CellCheckbox {...ctx} />,
            enableSorting: false,
            enableHiding: false,
            maxSize: 45,
        }),
        columnHelper.accessor('number', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellAction
                    {...ctx}
                    sidePanelName={CASH_COLLECTIONS_SIDE_PANEL}
                    singleRowSelection={singleRowSelection}
                    render={() => (
                        <div className="flex flex-col">
                            <span>{ctx.getValue()}</span>
                        </div>
                    )}
                />
            ),
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionNumber),
            },
            maxSize: 45,
            id: 'number',
        }),
        columnHelper.accessor('parking.name', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            enableHiding: true,
            id: 'parking',
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(payStationsMessages.parking),
                options: parkingsData?.results.map((parking) => {
                    return { value: parking.id, label: parking.name };
                }),
            },
        }),
        columnHelper.accessor('paystation.identifier', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            id: 'paystation',
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cashCollectionsMessages.cashCollectionsPayStation),
                options: payStationsData?.results.map((station) => {
                    return { value: station.id, label: station.identifier };
                }),
            },
        }),
        columnHelper.accessor('date', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionDate),
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => <CellBadge badgeOptions={badgeCollectionStatusOptions} {...props} />,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cashCollectionsMessages.cashCollectionStatus),
                badgeOptions: badgeCollectionStatusOptions,
            },
        }),
        columnHelper.accessor('total_amount', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return formatNumber(row.original.total_amount / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionTotalAmount),
            },
        }),
        columnHelper.accessor('collected_by', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionCollectedBy),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellCashCollectionActions
                    {...props}
                    sidePanelName={CASH_COLLECTIONS_SIDE_PANEL}
                    singleRowSelection={singleRowSelection}
                    deleteCallback={openDeleteModal}
                    editCallback={openEditModal}
                />
            ),
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: () => null,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                filterLabel: formatMessage(cashCollectionsMessages.cashCollectionType),
                options: Object.entries(cashCollectionTypeTranslationMap).map(([key, value]) => ({
                    value: key,
                    label: value,
                })),
            },
            id: 'type',
        }),
    ];

    const selectedRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!data || !firstSelected) return;
        return data.results.find((cards) => cards.id === firstSelected);
    }, [data, rowSelectionState]);

    const selectedCashCollectionId = selectedRowData?.id ?? '';

    return (
        <>
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                rowSelection={{
                    rowSelectionState: multiRowState,
                    setRowSelectionState: setMultiRowState,
                }}
                getRowId={(row) => row.id.toString()}
                semantics={{
                    title: formatMessage(cashCollectionsMessages.cashCollections),
                    entryName: formatMessage(cashCollectionsMessages.collection),
                    entryNames: formatMessage(cashCollectionsMessages.collections),
                    placeholder: formatMessage(cashCollectionsMessages.cashCollection),
                    messageBlockSubtitle: formatMessage(cashCollectionsMessages.multipleCashCollections),
                    initialColumnVisibility: { type: false },
                }}
                isError={isError}
                isLoading={isLoading}
                isMultiSelect
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry: permissionCashCollections ? NewCashCollection : undefined,
                }}
                enableLocalFilters={true}
            />
            <SidePanel onClose={clearSelection} name={CASH_COLLECTIONS_SIDE_PANEL}>
                <CashCollectionSidePanel id={selectedCashCollectionId} />
            </SidePanel>

            {isDeleteModalOpen && selectedCashCollection && (
                <ConfirmationModal
                    isModalOpen={isDeleteModalOpen}
                    setIsModalOpen={setIsDeleteModalOpen}
                    dialogDetails={{
                        title: { ...generalMessages.delete },
                        subTitle: { ...cashCollectionsMessages.cashCollectionDelete },
                        icon: ICON_MAP.DELETE,
                        confirmationMessage: { ...cashCollectionsMessages.cashCollectionDeleteConfirmation },
                    }}
                    callbackButton={{
                        variant: 'destructiveLight',
                        buttonTitle: generalMessages.delete,
                        callbackFn: () => {
                            return (
                                deleteCashCollection({ id: selectedCashCollection.id }),
                                setIsDeleteModalOpen(false)
                            );
                        },
                    }}
                />
            )}
            {isEditModalOpen && selectedCashCollection && (
                <EditCashCollection
                    selectedCashCollection={selectedCashCollection}
                    isEditModalOpen={isEditModalOpen}
                    setIsEditModalOpen={setIsEditModalOpen}
                />
            )}

            {pendingAction && pendingAction.id === 'edit_status' && (
                <EditCashCollectionStatus
                    form={form}
                    onSubmit={form.handleSubmit((data) => handleMultiSelectUpdateStatus(selectedIds, data.status))}
                    isModalOpen={!!pendingAction}
                    onOpenChange={() => {
                        setPendingAction(null);
                        form.reset();
                    }}
                />
            )}
        </>
    );
};

export default CashCollectionsTable;
